import { useState } from 'react';

const PASSWORD_HINT =
  'At least 8 characters, with uppercase, lowercase, a number, and a special character.';

function validatePassword(password, confirmPassword) {
  const errors = {};
  if (!password) {
    errors.password = 'Password is required';
  } else if (password.length < 8) {
    errors.password = 'Password must be at least 8 characters';
  } else if (!/[A-Z]/.test(password)) {
    errors.password = 'Password must contain at least one uppercase letter';
  } else if (!/[a-z]/.test(password)) {
    errors.password = 'Password must contain at least one lowercase letter';
  } else if (!/\d/.test(password)) {
    errors.password = 'Password must contain at least one digit';
  } else if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?`~]/.test(password)) {
    errors.password = 'Password must contain at least one special character';
  }
  if (!confirmPassword) {
    errors.confirmPassword = 'Please confirm your password';
  } else if (password !== confirmPassword) {
    errors.confirmPassword = 'Passwords do not match';
  }
  return errors;
}

export default function GuestCheckoutAccountModal({
  open,
  email,
  onClose,
  onContinueAsGuest,
  onCreateAccount,
  isSubmitting,
}) {
  const [mode, setMode] = useState('choose');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState({});

  if (!open) return null;

  const handleCreate = async (e) => {
    e.preventDefault();
    const nextErrors = validatePassword(password, confirmPassword);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) return;
    await onCreateAccount(password);
  };

  const resetAndClose = () => {
    setMode('choose');
    setPassword('');
    setConfirmPassword('');
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
      <div className="bg-card rounded-xl shadow-xl max-w-md w-full p-6 relative">
        <button
          type="button"
          onClick={resetAndClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground"
          aria-label="Close"
        >
          <i className="ri-close-line text-2xl"></i>
        </button>

        {mode === 'choose' ? (
          <>
            <h3 className="text-xl font-bold text-foreground mb-2 pr-8">
              Create an account?
            </h3>
            <p className="text-sm text-muted-foreground mb-6">
              Save this address and track your orders, or continue checkout as a guest.
            </p>
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setMode('create')}
                className="w-full px-4 py-3 bg-primary text-white rounded-lg text-sm font-semibold hover:brightness-110 transition-all"
              >
                Create account
              </button>
              <button
                type="button"
                onClick={onContinueAsGuest}
                className="w-full px-4 py-3 border border-border text-foreground rounded-lg text-sm font-semibold hover:bg-muted transition-colors"
              >
                Continue as guest
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={handleCreate}>
            <h3 className="text-xl font-bold text-foreground mb-2 pr-8">
              Set a password
            </h3>
            <p className="text-sm text-muted-foreground mb-5">
              We&apos;ll create your account with <span className="font-medium text-foreground">{email}</span> and keep you signed in.
            </p>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (errors.password) setErrors((prev) => ({ ...prev, password: '' }));
                  }}
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none ${
                    errors.password ? 'border-red-500' : 'border-border focus:border-primary'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.password ? (
                  <p className="text-red-500 text-xs mt-1">{errors.password}</p>
                ) : (
                  <p className="text-muted-foreground text-xs mt-1">{PASSWORD_HINT}</p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">
                  Confirm password <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (errors.confirmPassword) setErrors((prev) => ({ ...prev, confirmPassword: '' }));
                  }}
                  className={`w-full px-3 py-2.5 border rounded-lg text-sm outline-none ${
                    errors.confirmPassword ? 'border-red-500' : 'border-border focus:border-primary'
                  }`}
                  disabled={isSubmitting}
                />
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                )}
              </div>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 mt-6">
              <button
                type="submit"
                disabled={isSubmitting}
                className="flex-1 px-4 py-2.5 bg-primary text-white rounded-lg text-sm font-semibold hover:brightness-110 transition-all disabled:opacity-50"
              >
                {isSubmitting ? 'Creating account…' : 'Create account & continue'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setMode('choose');
                  setErrors({});
                }}
                disabled={isSubmitting}
                className="px-4 py-2.5 border border-border rounded-lg text-sm font-medium hover:bg-muted"
              >
                Back
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
